import React, { FC, useEffect, useMemo, useState } from 'react'
import { useAlert } from 'react-alert'
import { Helmet } from 'react-helmet'
import { useDispatch, useSelector } from 'react-redux'
import { Link, useHistory, useLocation, useParams } from 'react-router-dom'
import { addApplicationFavorite, delApplicationFavorite, fetchApplicationList } from '../../../states/containers/analyze/analyzeSlice'
import { updateCurrentAnalyzeSelectMember, updateCurrentAnalyzeSelectTeam } from '../../../states/containers/common/currentSlice'
import { RootState } from '../../../states/containers/store'
import { CompanyServices } from '../../../states/types/analyze'
import { LangValue } from '../../../states/types/common'
import { endOfMonth, format, nowDate, prevMonth, startOfMonth } from '../../../utils/date'
import { setPath, setUrlParam } from '../../../utils/url'
import ApplicationCardList from '../../components/analyze/ApplicationCardList'
import ReportCardList from '../../components/analyze/ReportCardList'
import Loading from '../../components/common/Loading'
import Tips from '../../components/common/Tips'
import CardList from '../../components/common/CardList'
import ANALYZE_REPORT from '../../constants/analyzeReport'
import EXPORT_LIST from '../../constants/export'
import { URL } from '../../constants/url'

const Index: FC = () => {
  const { language } = useParams<{ language: LangValue }>()
  const history = useHistory()
  const location = useLocation()
  const dispatch = useDispatch()
  const alert = useAlert()

  const {
    analyze,
  } = useSelector((state: RootState) => state)

  const [applicationList, setApplicationList] = useState<CompanyServices[]>()

  const startDate = useMemo(() => {
    if ((Number(startOfMonth(nowDate(), "YYYYMMDD")) + 7) < Number(nowDate("YYYYMMDD"))) {
      return startOfMonth(nowDate(), "YYYY-MM-DD")
    }
    return startOfMonth(prevMonth(nowDate()), "YYYY-MM-DD")
  }, [])

  const endDate = useMemo(() => {
    if ((Number(startOfMonth(nowDate(), "YYYYMMDD")) + 7) < Number(nowDate("YYYYMMDD"))) {
      return endOfMonth(nowDate(), "YYYY-MM-DD")
    }
    return endOfMonth(prevMonth(nowDate()), "YYYY-MM-DD")
  }, [])

  const clickApp = (name: string) => {
    dispatch(updateCurrentAnalyzeSelectTeam(''))
    dispatch(updateCurrentAnalyzeSelectMember(''))
    history.push(`${setPath(setUrlParam(
      URL.APP_ANALYZE_DETAIL.path,
      [
        { key: 'name', value: name },
      ],
    ), language)}?date=${startDate}`)
  }

  const clickFab = (name: string) => {
    if (!applicationList) return
    const target = applicationList.find((value) => value.name === name)
    if (!target) return
    setApplicationList(
      [...applicationList.map((value) => (value.name === name ? { ...value, favorite: !value.favorite } : value))],
    )
    const tmpName = target.company_service_name
      ? `${target.company_service_name}(${name})`
      : name
    if (target.favorite) {
      dispatch(
        delApplicationFavorite(
          { service_name: name },
          () => {
            alert.success(`${tmpName}のピンを外しました。`)
          },
        ),
      )
    } else {
      dispatch(
        addApplicationFavorite(
          { service_name: name },
          () => {
            alert.success(`${tmpName}にピンを刺しました。`)
          },
        ),
      )
    }
  }

  const clickReport = (type: string) => {
    dispatch(updateCurrentAnalyzeSelectTeam(''))
    dispatch(updateCurrentAnalyzeSelectMember(''))
    history.push(`${setPath(setUrlParam(
      URL.ANALYZE_REPORT_DETAIL.path,
      [
        { key: 'type', value: type },
      ],
    ), language)}?date=${startDate}`)
  }

  const clickContents = (url: string) => {
    history.push(`${setPath(url, language)}`)
  }

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [location])

  useEffect(() => {
    dispatch(fetchApplicationList(
      {
        start_date: startDate,
        end_date: endDate,
      },
    ))
  }, [])

  useEffect(() => {
    setApplicationList(analyze.applicationList?.company_services.filter((value) => value.favorite))
  }, [analyze.applicationList])

  return (
    <section className="analyze_page export_page">
      <Helmet>
        <title>Qasee - 組織分析</title>
      </Helmet>
      <section className="page_header">
        <h1>
          組織分析
        </h1>
      </section>
      <section className="page_contents content-area">
        <div className="content-block">
          <div className="block-header">
            <div className="title">
              <Tips
                description="特に注目したいアプリケーション分析でピン📌をクリックすると設定できます"
                className=""
              >
                <>
                  ピン留めしたアプリケーション分析
                </>
              </Tips>
            </div>
            <div
              style={{
                color: "#999999",
              }}
            >
              {`${format(startDate, "YYYY年MM月")}のデータを表示しています`}
            </div>
          </div>
          <div className="block-detail">
            {
              analyze.isLoading ? (
                <>
                  <Loading />
                </>
              ) : (
                <>
                  {
                    applicationList?.length ? (
                      <ApplicationCardList
                        clickAppCallback={clickApp}
                        clickFabCallback={clickFab}
                        list={applicationList}
                      />
                    ) : (
                      <div className="no_data_area">
                        <div className="no_data_text">
                          ピン留めしたアプリケーション分析はありません。
                        </div>
                      </div>
                    )
                  }
                  <div className="index_link_area">
                    <Link
                      to={setPath(URL.APP_ANALYZE.path, language)}
                    >
                      &gt;&gt; 全てのアプリケーション分析一覧
                    </Link>
                  </div>
                </>
              )
            }
          </div>
        </div>
      </section>
      <section className="page_contents content-area">
        <div className="content-block">
          <div className="block-header">
            <div className="title">
              分析レポート
            </div>
          </div>
          <div className="block-detail">
            <ReportCardList
              clickReportCallback={clickReport}
              list={ANALYZE_REPORT}
            />
            <div className="index_link_area">
              <Link
                to={setPath(URL.ANALYZE_REPORT.path, language)}
              >
                &gt;&gt; 全ての分析レポート一覧
              </Link>
            </div>
          </div>
        </div>
      </section>
    </section>
  )
}

export default Index
